import { useEffect, useState } from "react";
import { Reorder, useReducedMotion } from "motion/react";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  RotateCcw,
  Sparkles,
  X,
} from "lucide-react";
import type { useWorkspace } from "../hooks/useWorkspace";
import { PHONE_QUERY, useMediaQuery } from "../hooks/useMediaQuery";
import { ModalSheet } from "./ModalSheet";
import { SlotEditor } from "./SlotEditor";
import type { ImagePlanSlot } from "../types/workspace";
import {
  creativeDirectionDefaults,
  interpolate,
  localizeDirection,
  localizePlatform,
  localizePurposeForLocale,
  localizeRationale,
  t,
} from "../i18n";
import "./ImagePlan.css";

type Workspace = ReturnType<typeof useWorkspace>;

export function ImagePlan({ workspace }: { workspace: Workspace }) {
  const { state } = workspace;
  const reducedMotion = useReducedMotion();
  const visibleSlots = state.activeSlots;
  const selected =
    visibleSlots.find((slot) => slot.id === state.selectedSlotId) ??
    visibleSlots[0];
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    if (!isPhone) setEditorOpen(false);
  }, [isPhone]);

  const selectSlot = (id: string) => {
    workspace.selectSlot(id);
    if (isPhone) setEditorOpen(true);
  };

  const reorder = (next: ImagePlanSlot[]) => workspace.reorderActiveSlots(next);

  if (!visibleSlots.length)
    return (
      <section className="plan-canvas empty-plan">
        <p className="eyebrow">02 · {t(state.locale, "plan")}</p>
        <h1>{t(state.locale, "emptyPlanTitle")}</h1>
        <p>{t(state.locale, "emptyPlanSubtitle")}</p>
        <button
          className="primary-action"
          type="button"
          onClick={workspace.restoreAllSlots}
        >
          <RotateCcw size={16} /> {t(state.locale, "restoreTen")}
        </button>
      </section>
    );

  return (
    <section className="plan-canvas" aria-labelledby="plan-title">
      <div className="canvas-heading plan-heading">
        <div>
          <p className="eyebrow">02 · {t(state.locale, "plan")}</p>
          <h1 id="plan-title">{t(state.locale, "planTitle")}</h1>
          <p>
            {interpolate(t(state.locale, "planSubtitle"), {
              platform: localizePlatform(state.locale, state.platform.name),
            })}
          </p>
        </div>
        <div className="plan-count-control">
          <button
            type="button"
            aria-label={t(state.locale, "decreaseImageCount")}
            disabled={visibleSlots.length <= 1}
            onClick={() => workspace.setImageCount(visibleSlots.length - 1)}
          >
            −
          </button>
          <span className="selection-count">
            {interpolate(t(state.locale, "selectedCount"), {
              count: visibleSlots.length,
            })}
          </span>
          <button
            type="button"
            aria-label={t(state.locale, "increaseImageCount")}
            disabled={visibleSlots.length >= 10}
            onClick={() => workspace.setImageCount(visibleSlots.length + 1)}
          >
            +
          </button>
        </div>
      </div>
      <div className="plan-layout">
        <div>
          <Reorder.Group
            axis="y"
            values={visibleSlots}
            onReorder={reorder}
            className="plan-cards"
          >
            {visibleSlots.map((slot, index) => (
              <Reorder.Item
                key={slot.id}
                value={slot}
                transition={reducedMotion ? { duration: 0 } : undefined}
                className={
                  selected?.id === slot.id ? "plan-card selected" : "plan-card"
                }
              >
                <button
                  className="card-select"
                  type="button"
                  onClick={() => selectSlot(slot.id)}
                  aria-label={`${t(state.locale, "imagePurpose")}: ${localizePurposeForLocale(state.locale, slot.purpose)}`}
                >
                  <span className="plan-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>
                    <strong>
                      {localizePurposeForLocale(state.locale, slot.purpose)}
                    </strong>
                    <small>
                      {localizeDirection(state.locale, slot.direction)} ·{" "}
                      {localizeRationale(state.locale, slot.rationale)}
                    </small>
                  </span>
                </button>
                <div className="card-actions">
                  <span className="drag-handle" aria-hidden="true">
                    <GripVertical size={16} />
                  </span>
                  <button
                    type="button"
                    aria-label={`${t(state.locale, "moveUp")} ${localizePurposeForLocale(state.locale, slot.purpose)}`}
                    disabled={index === 0}
                    onClick={() => workspace.moveSlot(slot.id, -1)}
                  >
                    <ChevronUp size={15} />
                  </button>
                  <button
                    type="button"
                    aria-label={`${t(state.locale, "moveDown")} ${localizePurposeForLocale(state.locale, slot.purpose)}`}
                    disabled={index === visibleSlots.length - 1}
                    onClick={() => workspace.moveSlot(slot.id, 1)}
                  >
                    <ChevronDown size={15} />
                  </button>
                  <button
                    type="button"
                    aria-label={`${t(state.locale, "remove")} ${localizePurposeForLocale(state.locale, slot.purpose)}`}
                    onClick={() => workspace.toggleSlot(slot.id)}
                  >
                    <X size={15} />
                  </button>
                </div>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        </div>
        {selected && !isPhone && (
          <aside
            aria-label={`${localizePurposeForLocale(state.locale, selected.purpose)} ${t(state.locale, "imagePurpose")}`}
          >
            <SlotEditor
              slot={selected}
              locale={state.locale}
              onUpdate={(patch) => workspace.updateSlot(selected.id, patch)}
            />
          </aside>
        )}
        {selected && (
          <ModalSheet
            open={isPhone && editorOpen}
            title={`${String(visibleSlots.findIndex((slot) => slot.id === selected.id) + 1).padStart(2, "0")} · ${localizePurposeForLocale(state.locale, selected.purpose)}`}
            closeLabel={t(state.locale, "close")}
            onClose={() => setEditorOpen(false)}
            className="slot-editor-sheet"
            lang={state.locale}
          >
            <SlotEditor
              slot={selected}
              locale={state.locale}
              onUpdate={(patch) => workspace.updateSlot(selected.id, patch)}
            />
            <button
              className="primary-action sheet-done"
              type="button"
              onClick={() => setEditorOpen(false)}
            >
              {t(state.locale, "done")}
            </button>
          </ModalSheet>
        )}
      </div>
      {workspace.preview && (
        <details className="prompt-preview" open>
          <summary>
            {interpolate(t(state.locale, "compiledPromptPreview"), {
              count: workspace.preview.prompts.length,
            })}
          </summary>
          <pre>
            {workspace.preview.prompts
              .map((prompt) => `SLOT ${prompt.index}\n${prompt.prompt}`)
              .join("\n\n---\n\n")}
          </pre>
        </details>
      )}
      <div className="creative-direction">
        <label htmlFor="creative-direction">
          {t(state.locale, "creativeDirection")}{" "}
          <span>{t(state.locale, "creativeDirectionHint")}</span>
        </label>
        <textarea
          id="creative-direction"
          placeholder={creativeDirectionDefaults[state.locale]}
          value={state.creativeDirection}
          onChange={(event) =>
            workspace.updateCreativeDirection(event.target.value)
          }
        />
      </div>
      <div
        className={`plan-footer${!workspace.authenticated ? " is-gated" : ""}`}
      >
        <p>
          <Sparkles size={17} />
          {t(state.locale, "planAdvice")}
        </p>
        <div className="generation-actions">
          <div className="plan-footer-actions">
            <button
              className="outline-action prompt-button"
              type="button"
              onClick={workspace.previewPrompts}
              disabled={workspace.busy || !workspace.authenticated}
            >
              {workspace.busy ? (
                <>
                  <span className="button-spinner" aria-hidden="true" />{" "}
                  {t(state.locale, "preparingPrompts")}
                </>
              ) : (
                t(state.locale, "previewPrompts")
              )}
            </button>
            <button
              className="primary-action prompt-button"
              type="button"
              onClick={workspace.generateImages}
              disabled={workspace.busy || !workspace.authenticated}
            >
              {interpolate(t(state.locale, "generateCount"), {
                count: visibleSlots.length,
              })}{" "}
              <span>→</span>
            </button>
          </div>
        </div>
        {!workspace.authenticated && (
          <div className="generation-login-overlay" role="status">
            {t(state.locale, "loginRequired")}
          </div>
        )}
      </div>
    </section>
  );
}
